// pages/ToshbaDomainDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import Accordion from '../components/Accordion';

export default function ToshbaDomainDetail() {
  const { domain } = useParams();
  const [subtopics, setSubtopics] = useState([]);
  const [domainExplanation, setDomainExplanation] = useState(null);
  const [typeCardImages, setTypeCardImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const domainMap = {
    'mishna': 'משנה',
    'gmara': 'גמרא',
    'halacha': 'הלכה'
  };

  const domainName = domainMap[domain] || domain;

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // First, get the discipline to fetch domain explanations and type card images
        const disciplinesRes = await fetchWithAuth('/api/disciplines/');
        if (disciplinesRes.ok) {
          const disciplines = await disciplinesRes.json();
          const toshbaDiscipline = disciplines.find(d => d.name.includes('תושב"ע'));
          
          if (toshbaDiscipline) {
            // Fetch domain info including explanations
            const domainsRes = await fetchWithAuth(`/api/disciplines/${toshbaDiscipline.id}/toshba-domains/`);
            if (domainsRes.ok) {
              const domains = await domainsRes.json();
              const currentDomain = domains.find(d => d.route === domain);
              if (currentDomain && currentDomain.explanation) {
                if (!ignore) setDomainExplanation(currentDomain.explanation);
              }
            }
            
            // Fetch type card images
            const imagesRes = await fetchWithAuth(`/api/disciplines/${toshbaDiscipline.id}/toshba-type-card-images/`);
            if (imagesRes.ok) {
              const imagesData = await imagesRes.json();
              if (!ignore) setTypeCardImages(imagesData.images || {});
            }
          }
        }

        // Filter subtopics by domain name (across all classes)
        if (domain === 'mishna') {
          const res = await fetchWithAuth(`/api/subtopic-mishna/?domain_name=${domainName}`);
          if (res.ok) {
            const data = await res.json();
            if (!ignore) setSubtopics(data.filter(s => s.is_enabled));
          }
        } else if (domain === 'gmara') {
          const res = await fetchWithAuth(`/api/subtopic-gmara/?domain_name=${encodeURIComponent(domainName)}`);
          if (res.ok) {
            const data = await res.json();
            if (!ignore) setSubtopics(data.filter(s => s.is_enabled));
          }
        } else if (domain === 'halacha') {
          const res = await fetchWithAuth(`/api/subtopic-halacha/?domain_name=${domainName}`);
          if (res.ok) {
            const data = await res.json();
            if (!ignore) setSubtopics(data.filter(s => s.is_enabled));
          }
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת התוכן');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [domain, domainName]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/toshba/classes', label: 'תושב"ע' },
          { label: domainName },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {domainName}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/toshba/classes"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לתושב"ע
        </Link>
      </p>

      {/* Explanation accordion */}
      {domainExplanation && (
        <Accordion 
          title="הסבר" 
          content={domainExplanation} 
          className="mb-8 max-w-4xl mx-auto" 
        />
      )}

      {/* Type-based cards section - appears first */}
      {(domain === 'gmara' || domain === 'mishna' || domain === 'halacha') && (
        <div className="grid-autofit mb-8">
          {/* For gmara, show two fixed type cards */}
          {domain === 'gmara' && (
            <>
              <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                <Link
                  to="/toshba/classes/gmara/foundations"
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {typeCardImages.gmara_foundations ? (
                    <img
                      src={typeCardImages.gmara_foundations}
                      alt="הישגים בהוראת גמרא"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to="/toshba/classes/gmara/foundations"
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      הישגים בהוראת גמרא
                    </Link>
                  </h3>
                </div>
              </article>
              <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                <Link
                  to="/toshba/classes/gmara/structure"
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {typeCardImages.gmara_structure ? (
                    <img
                      src={typeCardImages.gmara_structure}
                      alt="המלצות להוראה מיטבית"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to="/toshba/classes/gmara/structure"
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      המלצות להוראה מיטבית
                    </Link>
                  </h3>
                </div>
              </article>
            </>
          )}
          {/* For mishna, show two fixed type cards */}
          {domain === 'mishna' && (
            <>
              <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                <Link
                  to="/toshba/classes/mishna/plan"
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {typeCardImages.mishna_plan ? (
                    <img
                      src={typeCardImages.mishna_plan}
                      alt="הישגים בהוראת משנה"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to="/toshba/classes/mishna/plan"
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      הישגים בהוראת משנה
                    </Link>
                  </h3>
                </div>
              </article>
              <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                <Link
                  to="/toshba/classes/mishna/foundations"
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {typeCardImages.mishna_foundations ? (
                    <img
                      src={typeCardImages.mishna_foundations}
                      alt="המלצות להוראה מיטבית"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to="/toshba/classes/mishna/foundations"
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      המלצות להוראה מיטבית
                    </Link>
                  </h3>
                </div>
              </article>
            </>
          )}
          {/* For halacha, show two fixed type cards */}
          {domain === 'halacha' && (
            <>
              <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                <Link
                  to="/toshba/classes/halacha/plan"
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {typeCardImages.halacha_plan ? (
                    <img
                      src={typeCardImages.halacha_plan}
                      alt="הישגים בהוראת הלכה"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to="/toshba/classes/halacha/plan"
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      הישגים בהוראת הלכה
                    </Link>
                  </h3>
                </div>
              </article>
              <article className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1">
                <Link
                  to="/toshba/classes/halacha/skills"
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {typeCardImages.halacha_skills ? (
                    <img
                      src={typeCardImages.halacha_skills}
                      alt="המלצות להוראה מיטבית"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to="/toshba/classes/halacha/skills"
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      המלצות להוראה מיטבית
                    </Link>
                  </h3>
                </div>
              </article>
            </>
          )}
        </div>
      )}

      {/* Subtopic cards section - appears below type cards */}
      <div className="grid-autofit">
        {/* Show all subtopics */}
        {subtopics.map(subtopic => {
          const subtopicId = subtopic.id;
          return (
            <article
              key={subtopic.id}
              className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
            >
              <Link
                to={`/toshba/classes/${domain}/${subtopicId}`}
                className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                style={{ aspectRatio: '3/2' }}
              >
                {subtopic.image ? (
                  <img
                    src={subtopic.image}
                    alt={subtopic.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </Link>
              <div className="p-4 md:p-5">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                  <Link
                    to={`/toshba/classes/${domain}/${subtopicId}`}
                    className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {subtopic.name}
                  </Link>
                </h3>
                {subtopic.toshba_class_name && (
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                    {subtopic.toshba_class_name}
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {subtopics.length === 0 && !['gmara', 'mishna', 'halacha'].includes(domain) && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין נושאים להצגה.
        </p>
      )}

      {/* Email for submitting materials */}
      {(domain === 'gmara' || domain === 'mishna' || domain === 'halacha') && (
        <div className="max-w-2xl mx-auto mt-8 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
          <p className="text-center text-secondary-700 dark:text-secondary-300 mb-2">
            יש לכם חומרים נוספים לשיתוף?
          </p>
          <p className="text-center">
            <a
              href={`mailto:haifakodesh@gmail.com?subject=שיתוף חומרים - תושב"ע - ${domainName}`}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline"
            >
              haifakodesh@gmail.com
            </a>
          </p>
        </div>
      )}
    </div>
  );
}
